import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { LocalNotifications } from '@capacitor/local-notifications';
import { Preferences } from '@capacitor/preferences';
import { Browser } from '@capacitor/browser';
import { deviceTokens, pushOpened } from 'common/utils/network/endpoints';
import { baseApi } from 'common/store/baseApi';
import type { AppDispatch } from 'common/store/store';
// Safe as a static import since 36f76e3: eventTriggers no longer touches the
// store (it is bound at creation), so no cycle exists in either direction.
import { resolveInAppPath } from 'common/utils/appLinks';
import { trackEvent, MixpanelEventName } from 'common/utils/mixpanel/eventTriggers';

// The push token this device last registered with the backend, kept so
// logout can unregister the exact token.
export const PUSH_TOKEN_KEY = 'pushDeviceToken';

export const PushDeviceServiceAPI = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    registerDeviceAction: builder.mutation<void, {
      token: string; platform: string; environment: string; push_reminders: boolean;
      country: string | null; timezone: string | null;
    }>({
      query: (body) => ({
        url: deviceTokens,
        method: 'POST',
        body,
      }),
    }),
    pushOpenedAction: builder.mutation<void, { id: string }>({
      query: ({ id }) => ({
        url: pushOpened.replace('%id%', id),
        method: 'POST',
      }),
    }),
  }),
});

// Region from the phone's own locale (en-GB -> GB): no location permission,
// no prompt, good enough for "UK announcement" targeting.
const getDeviceRegion = (): string | null => {
  try {
    const tag = navigator.language || '';
    const region = new Intl.Locale(tag).region || null;
    return region && /^[A-Za-z]{2}$/.test(region) ? region.toUpperCase() : null;
  } catch {
    return null;
  }
};

const getDeviceTimezone = (): string | null => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
};

// Set by the native shell (MainViewController): 'sandbox' for debug builds,
// 'production' for store builds. Android has one FCM lane, so production.
const getApnsEnvironment = async (): Promise<string> => {
  try {
    const { value } = await Preferences.get({ key: 'apnsEnvironment' });
    return value === 'sandbox' ? 'sandbox' : 'production';
  } catch {
    return 'production';
  }
};

// A notification tap can deep-link into the app (a document, a screen).
// The router lives inside React, so the target path is queued here and
// consumed by PersistLogin (which also listens for taps while running).
export const OPEN_DOCUMENT_EVENT = 'doqit:open-document';
let pendingPath: string | null = null;
let pendingPathQueuedAt: number | null = null;

// How long a queued path is allowed to sit unconsumed before it's treated
// as stale and discarded rather than routed. The two original callers
// (a push tap, a universal link) both imply the device was just actively
// used, so this never mattered in practice. The home-screen app shortcut
// (AppLifecycleHandler.tsx) broke that assumption - it's cold-reachable by
// someone who has never logged in, or hasn't logged in for days, on a
// device that may not even be theirs. Without a TTL, a shortcut tapped
// once on a shared, logged-out device would sit queued indefinitely and
// fire on whichever account next successfully logs in - a different
// person's session. 10 minutes comfortably covers "user opens the app
// shortly after tapping the shortcut and logs in", while bounding the
// "queued for a stranger's later login" window to something short.
const PENDING_PATH_TTL_MS = 10 * 60 * 1000;

export const consumePendingPath = (): string | null => {
  const path = pendingPath;
  const queuedAt = pendingPathQueuedAt;
  pendingPath = null;
  pendingPathQueuedAt = null;
  if (path == null) return null;
  if (queuedAt != null && Date.now() - queuedAt > PENDING_PATH_TTL_MS) {
    return null;
  }
  return path;
};

// Exported so other entry points that land on an in-app path (universal
// links, in AppLifecycleHandler.tsx) can reuse the same auth-gated queue
// instead of navigating directly, which would race PersistLogin's own
// auth check on a cold start.
export const queueNavigation = (path: string) => {
  pendingPath = path;
  pendingPathQueuedAt = Date.now();
  window.dispatchEvent(new CustomEvent(OPEN_DOCUMENT_EVENT));
};

// Drops any queued-but-unconsumed path without routing it. Called when
// credentials are cleared (authSlice's clearCredentials reducer) so a
// shortcut/push/universal-link path queued under one session can never be
// consumed by a DIFFERENT session that logs in afterwards on the same
// device - most concretely relevant to the app-shortcut case, which (unlike
// a push tap or a universal link) can be triggered by someone who was never
// authenticated on this device at all. Exported rather than leaving callers
// to reach into the module-level variable directly.
export const clearPendingPath = () => {
  pendingPath = null;
  pendingPathQueuedAt = null;
};

// Fired when this device switches back to local reminder scheduling
// (settings row toggled OFF). reminderNotification.ts listens for this to
// reset its own once-per-session reconcile guard - a plain function import
// the other way (this module importing from reminderNotification.ts) would
// create a circular dependency, since that module already imports
// remindersHandledByPush from here. A window event keeps the dependency
// one-directional, matching the existing OPEN_DOCUMENT_EVENT idiom above.
export const REMINDERS_SWITCHED_TO_LOCAL_EVENT = 'doqit:reminders-switched-to-local';

// Reminder delivery mode. Once this device successfully registers for push
// it flips to 'push': the server sweeps and delivers reminders to ALL the
// user's devices, and this device stops scheduling local ones (cancelling
// any it scheduled in the past, once). Devices that never register keep
// local scheduling exactly as before.
const REMINDERS_MODE_KEY = 'remindersMode';

export const remindersHandledByPush = async (): Promise<boolean> => {
  try {
    const { value } = await Preferences.get({ key: REMINDERS_MODE_KEY });
    return value === 'push';
  } catch {
    return false;
  }
};

// The user's own choice from the Notifications settings row, kept
// independent of REMINDERS_MODE_KEY so an OS-driven token refresh (the
// 'registration' listener below fires again any time the platform reissues
// a token, not just on first install) can tell "this device has never
// registered" apart from "this device registered, but the user turned
// reminder push off" - without this, a silent re-registration would
// hardcode push_reminders back to true and flip local mode back to push,
// undoing the user's choice both locally and on the server.
const REMINDER_PUSH_OPT_OUT_KEY = 'reminderPushOptOut';

const isReminderPushOptedOut = async (): Promise<boolean> => {
  try {
    const { value } = await Preferences.get({ key: REMINDER_PUSH_OPT_OUT_KEY });
    return value === 'true';
  } catch {
    return false;
  }
};

const switchToPushReminders = async (): Promise<void> => {
  if (await remindersHandledByPush()) {
    return;
  }
  try {
    const { notifications } = await LocalNotifications.getPending();
    if (notifications.length > 0) {
      await LocalNotifications.cancel({ notifications: notifications.map(({ id }) => ({ id })) });
    }
    await Preferences.set({ key: REMINDERS_MODE_KEY, value: 'push' });
  } catch (error) {
    // Stay in local mode: reminders keep working the old way, and the
    // switch is retried on the next successful registration.
    console.warn('Switch to push reminders failed (non-fatal):', error);
  }
};

// Mirror of switchToPushReminders for the opposite direction (master toggle
// turned OFF). There's nothing to cancel here - local scheduling resuming
// is the point - just the mode flip itself, so scheduleReminderNotificationIfFuture
// (gated on remindersHandledByPush()) starts scheduling again the next time
// a reminder-bearing doc is created/edited. Rebuilding the FULL local queue
// (every existing reminder, not just new ones - this module never has that
// list, it only exists as page-local state on the dashboard) needs
// reconcileLocalReminders to actually run rather than get skipped by its
// once-per-session guard - the REMINDERS_SWITCHED_TO_LOCAL_EVENT dispatch
// below resets that guard (see reminderNotification.ts's listener), so the
// next dashboard mount (an ordinary SPA navigation back to /dashboard, not
// a cold start - see the ref comments at that call site) rebuilds the
// queue from a fresh, complete owned-doc fetch.
const switchToLocalReminders = async (): Promise<void> => {
  try {
    await Preferences.set({ key: REMINDERS_MODE_KEY, value: 'local' });
    window.dispatchEvent(new CustomEvent(REMINDERS_SWITCHED_TO_LOCAL_EVENT));
  } catch (error) {
    console.warn('Switch to local reminders failed (non-fatal):', error);
  }
};

let listenersAttached = false;

// Register this device for push. Safe to call on every authed boot:
// - no-op on web and when the OS notification permission isn't granted
//   (the reminder primer, features/NotificationPrimer, handles asking, and
//   calls this again once the user has allowed);
// - re-POSTing an unchanged token is a cheap upsert that re-points the
//   device at the current account and refreshes last_seen_at;
// - on Android without google-services.json this lands in
//   registrationError and is logged, never thrown (feature ships dark).
export const initPushRegistration = async (dispatch: AppDispatch) => {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  try {
    // Nothing prompts at boot any more: with 'prompt' we return here and
    // registration happens after the user allows via the reminder primer.
    const permStatus = await PushNotifications.checkPermissions();
    if (permStatus.receive !== 'granted') {
      return;
    }

    if (!listenersAttached) {
      listenersAttached = true;

      await PushNotifications.addListener('registration', async ({ value }) => {
        // Dynamic import: a static one creates an import cycle through the
        // store (this file is itself in the store's dependency chain).
        trackEvent(MixpanelEventName.pushDeviceRegistered, { platform: Capacitor.getPlatform() });
        try {
          await Preferences.set({ key: PUSH_TOKEN_KEY, value });
          // This listener fires on every reissue of the platform token, not
          // just first install (an OS-driven refresh, unrelated to the
          // user's own choice) - read the opt-out the settings row wrote so
          // a silent refresh can't re-register with push_reminders hardcoded
          // true and flip the device back to push mode.
          const optedOut = await isReminderPushOptedOut();
          const registration = dispatch(
            PushDeviceServiceAPI.endpoints.registerDeviceAction.initiate({
              token: value,
              platform: Capacitor.getPlatform(),
              environment: Capacitor.getPlatform() === 'ios' ? await getApnsEnvironment() : 'production',
              push_reminders: !optedOut,
              country: getDeviceRegion(),
              timezone: getDeviceTimezone(),
            }),
          );
          registration.unwrap()
            .then(() => (optedOut ? undefined : switchToPushReminders()))
            .catch((error) => {
              console.warn('Push token registration failed (non-fatal):', error);
            })
            .finally(() => registration.reset());
        } catch (error) {
          console.warn('Push token registration failed (non-fatal):', error);
        }
      });

      await PushNotifications.addListener('registrationError', (error) => {
        // Expected on simulators and on Android until google-services.json
        // is added — never fatal.
        console.warn('Push registration error (non-fatal):', error);
      });

      // Fires when the user taps a notification (Capacitor retains a tap
      // that launched the app until a listener consumes it, so attaching
      // post-auth is safe on a cold start too).
      await PushNotifications.addListener('pushNotificationActionPerformed', ({ notification }) => {
        try {
          const data = (notification && notification.data) || {};

          // Composer pushes carry their id: report the tap so the /push
          // page can show open counts. Fire and forget.
          const adminPushId = data.adminPushId ?? data.data?.adminPushId ?? null;
          if (adminPushId) {
            const beacon = dispatch(
              PushDeviceServiceAPI.endpoints.pushOpenedAction.initiate({ id: String(adminPushId) }),
            );
            beacon.unwrap().catch(() => {}).finally(() => beacon.reset());
          }

          const documentId = data.documentId ?? data.data?.documentId ?? null;
          const rawLink = data.link ?? data.data?.link ?? null;
          const composerRoute = typeof rawLink === 'string' && rawLink.startsWith('/')
            ? resolveInAppPath(rawLink)
            : null;
          trackEvent(MixpanelEventName.pushNotificationOpened, {
            has_admin_push_id: !!adminPushId,
            has_document_id: !!documentId,
            platform: Capacitor.getPlatform(),
            route: composerRoute ?? 'none',
          });
          if (documentId) {
            // Quick Save of a password-protected file: open the document
            // with the unlock prompt already up (UnlockToRead.tsx).
            const action = data.action ?? data.data?.action ?? null;
            const suffix = action === 'unlock' ? '?unlock=1' : '';
            queueNavigation(`/editDocuments/${String(documentId)}${suffix}`);
            return;
          }

          // Composer sends may carry a destination: an in-app screen
          // (/reminders, /documents) or an https page for the browser.
          const link = data.link ?? data.data?.link ?? null;
          if (typeof link === 'string' && link.startsWith('https://')) {
            Browser.open({ url: link }).catch(() => {});
          } else if (composerRoute) {
            queueNavigation(composerRoute);
          }
        } catch (error) {
          console.warn('Push tap handling failed (non-fatal):', error);
        }
      });
    }

    await PushNotifications.register();
  } catch (error) {
    console.warn('Push registration init failed (non-fatal):', error);
  }
};

// Result of a master-toggle flip, typed so the settings row can pick an
// exact reason string without inspecting an error object:
// - 'no-token': nothing registered on this device (permission denied, or
//   registration hasn't landed yet) - the row stays disabled, this is not
//   a failure of the toggle attempt itself.
// - 'error': the POST failed (network, server); the caller should revert
//   its optimistic UI state.
// - 'partial': the server accepted the new push_reminders value, but the
//   local remindersMode Preference write that's supposed to mirror it
//   (switchToPushReminders/switchToLocalReminders, both best-effort and
//   non-throwing) didn't stick - verified below by re-reading it back. The
//   caller should treat this exactly like 'error' (revert + refresh),
//   since showing the toggle as flipped would contradict this device's own
//   local state.
// - 'ok': the token's push_reminders flag and the local remindersMode are
//   both updated, verified.
export type SetReminderPushEnabledResult =
  | { status: 'ok' }
  | { status: 'no-token' }
  | { status: 'error' }
  | { status: 'partial' };

// Master reminder-push toggle (Account > Notifications). Re-POSTs the same
// device-token payload `initPushRegistration`'s registration listener sends
// (updateOrCreate keyed on token - no new endpoint), just flipping
// push_reminders, then swings the local remindersMode Preference the same
// direction: ON reuses switchToPushReminders (cancels any pending locals),
// OFF reuses switchToLocalReminders (see its comment re: queue rebuild
// timing). Also persists the choice itself (REMINDER_PUSH_OPT_OUT_KEY) so a
// later OS-driven token refresh can't silently undo it (see the
// 'registration' listener above). Never throws: every failure resolves to
// a typed result instead.
export const setReminderPushEnabled = async (
  dispatch: AppDispatch,
  enabled: boolean,
): Promise<SetReminderPushEnabledResult> => {
  const token = await getStoredPushToken();
  if (!token) {
    return { status: 'no-token' };
  }

  try {
    const registration = dispatch(
      PushDeviceServiceAPI.endpoints.registerDeviceAction.initiate({
        token,
        platform: Capacitor.getPlatform(),
        environment: Capacitor.getPlatform() === 'ios' ? await getApnsEnvironment() : 'production',
        push_reminders: enabled,
        country: getDeviceRegion(),
        timezone: getDeviceTimezone(),
      }),
    );
    try {
      await registration.unwrap();
    } finally {
      registration.reset();
    }

    await Preferences.set({ key: REMINDER_PUSH_OPT_OUT_KEY, value: enabled ? 'false' : 'true' });

    if (enabled) {
      await switchToPushReminders();
    } else {
      await switchToLocalReminders();
    }

    // switchTo*Reminders swallow their own Preferences write failures
    // (non-fatal there - a device stuck in its old mode after a push
    // registration is still functional). Here, the toggle's whole point IS
    // that mode flip, so verify it actually landed rather than trust the
    // happy path.
    const modeMatches = (await remindersHandledByPush()) === enabled;
    if (!modeMatches) {
      return { status: 'partial' };
    }

    return { status: 'ok' };
  } catch (error) {
    console.warn('setReminderPushEnabled failed (non-fatal):', error);
    return { status: 'error' };
  }
};

export const getStoredPushToken = async (): Promise<string | null> => {
  try {
    const { value } = await Preferences.get({ key: PUSH_TOKEN_KEY });
    return value || null;
  } catch {
    return null;
  }
};

export const clearStoredPushToken = async (): Promise<void> => {
  try {
    await Preferences.remove({ key: PUSH_TOKEN_KEY });
  } catch {
    // best-effort
  }
};
